import { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useRouter } from 'next/router';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { Button } from '../_Button/Button';

import styles from './Accordion.module.css';

type AccordionProps = {
	children: JSX.Element[];
	sectionClass?: string;
	headerClass?: string;
	bodyClass?: string;
	startOpen?: boolean;
	activeStart?: number;
	activeEnd?: number;
	ancestorState?: boolean;
	setOpen?: boolean;
	active?: boolean;
    setActive?: (active: boolean) => void;
    onClick?: (active: boolean, setActive: (active: boolean) => void) => void;
};

const Accordion = (props: AccordionProps): JSX.Element => {
	const isEditing = IsSitecoreEditing();

	const header = useRef<HTMLButtonElement>(null);
	const body = useRef<HTMLDivElement>(null);
	const bodyInner = useRef<HTMLDivElement>(null);

	const router = useRouter();
	const queryParams = new URLSearchParams(router.asPath.substring(router.asPath.lastIndexOf('?') + 1));
	const isPdfDownload = queryParams.get('timestamp') !== null;

	const [openAccordion, setOpenAccordion] = useState(props.startOpen ? false : true);
	const [activeClass, setActiveClass] = useState('inactive');
	const [disableCollapse, setDisableCollapse] = useState(false);
	const [windowWidth, setWindowWidth] = useState(globalThis.window ? window.innerWidth : 0);
	const [initialised, setInitialised] = useState(false);

	const min = props.activeStart ? props.activeStart : 0;
	const max = props.activeEnd ? props.activeEnd : windowWidth;

	const active = props.active ?? openAccordion;
    const setActive = props.setActive ?? setOpenAccordion;

	const contentHeight = () => {
		if (!bodyInner.current) {
			return;
		}

		const el = bodyInner.current;

		return Math.ceil(el.getBoundingClientRect().height);
	};

	const heightValue = () => {
		if (disableCollapse || !contentHeight()) {
			return undefined;
		}

		return active ? `${contentHeight()}px` : '0px';
	};

	useEffect(() => {
		setDisableCollapse(windowWidth >= min && windowWidth <= max ? false : true);
	}, [max, min, windowWidth]);

	useEffect(() => {
		function resizeHandler() {
			setWindowWidth(window.innerWidth);
		}

		setWindowWidth(window.innerWidth);

		window.addEventListener('resize', resizeHandler);

		return () => window.removeEventListener('resize', resizeHandler);
	}, []);

	useEffect(() => {
		setActive(disableCollapse ? true : false);
	}, [disableCollapse, windowWidth]);

	useEffect(() => {
		if (props.ancestorState === false) {
			setActive(false);
		}
	}, [props.ancestorState]);

	useEffect(() => {
		setActive(props.setOpen ? props.setOpen : false);
	}, [props.setOpen]);

	useEffect(() => {
		if (active || initialised) {
			return;
		}

		setInitialised(true);
	}, [active, initialised]);

	return (
		<div className={`${props.sectionClass ? props.sectionClass : ''} ${styles.section} ${activeClass}`}>
			<Button
				title="Accordion"
				buttonRef={header}
				className={`${styles.header} ${props.headerClass ? props.headerClass : ''}`}
				disabled={disableCollapse}
				onClick={props.onClick ? () => props.onClick?.(active, setActive) : () => setActive(!active)}
			>
				<>{props.children[0]}</>
			</Button>

			{isEditing || isPdfDownload ? (
				<div className={`${styles.bodyInner} ${props.bodyClass ? props.bodyClass : ''}`} ref={bodyInner}>
					{props.children.map((child, index) => {
						return index > 0 ? child : '';
					})}
				</div>
			) : (
				<CSSTransition
					nodeRef={body}
					in={active}
					timeout={300}
					classNames={{ ...styles }}
					onEntering={() => setActiveClass('to-active')}
					onEntered={() => setActiveClass('active')}
					onExiting={() => setActiveClass('to-inactive')}
					onExited={() => setActiveClass('inactive')}
				>
					<div
						ref={body}
						className={`${styles.body} accordion-body ${initialised ? ' initialised' : ''}`}
						style={{ height: heightValue() }}
					>
						<div className={`${styles.bodyInner} ${props.bodyClass ? props.bodyClass : ''}`} ref={bodyInner}>
							{props.children.map((child, index) => {
								return index > 0 ? child : '';
							})}
						</div>
					</div>
				</CSSTransition>
			)}
		</div>
	);
};

export default Accordion;